import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { alpha, useTheme } from '@mui/material';
import { InterrogationStopRecordingDialog } from './InterrogationStopRecordingDialog';

interface InterrogationListenPanelProps {
  isListening: boolean;
  isConnecting: boolean;
  isCompleted: boolean;
  hasStartedBefore: boolean;
  onStart: () => void;
  onStop: () => void;
}

export function InterrogationListenPanel({
  isListening,
  isConnecting,
  isCompleted,
  hasStartedBefore,
  onStart,
  onStop,
}: InterrogationListenPanelProps) {
  const theme = useTheme();
  const [stopDialogOpen, setStopDialogOpen] = useState(false);

  if (isCompleted) {
    return null;
  }

  const handleConfirmStop = () => {
    setStopDialogOpen(false);
    onStop();
  };

  return (
    <>
      <Box
        sx={{
          bgcolor: theme.palette.background.paper,
          px: 2,
          pb: 'max(16px, env(safe-area-inset-bottom, 16px))',
          flexShrink: 0,
        }}
      >
        <Box sx={{ maxWidth: '960px', mx: 'auto' }}>
          {isListening ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                px: 1.5,
                py: 1.25,
                borderRadius: '10px',
                bgcolor: alpha(theme.palette.error.main, 0.06),
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Transcribing meeting audio
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Speech appears in the transcript above. Pause when the conversation breaks.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="warning"
                startIcon={<StopIcon />}
                onClick={() => setStopDialogOpen(true)}
                sx={{ flexShrink: 0, textTransform: 'none' }}
              >
                Stop recording
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<MicIcon />}
                onClick={onStart}
                disabled={isConnecting}
                sx={{ textTransform: 'none' }}
              >
                {isConnecting
                  ? 'Connecting…'
                  : hasStartedBefore
                    ? 'Resume recording'
                    : 'Start recording'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {hasStartedBefore
                  ? 'Recording is paused. Resume to continue capturing speech.'
                  : 'Capture the meeting; the transcript builds in real time above.'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <InterrogationStopRecordingDialog
        open={stopDialogOpen}
        onCancel={() => setStopDialogOpen(false)}
        onConfirm={handleConfirmStop}
      />
    </>
  );
}
